import { Road, POI, BusinessNode, NeighborhoodDNA } from '../domain/types.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';

export class NodeGenerator {
  public static generateNodes(
    roads: Road[],
    pois: POI[],
    dna: NeighborhoodDNA
  ): BusinessNode[] {
    const candidates: BusinessNode[] = [];
    const minDedupeDist = GAME_CONFIG.world.nodeDedupeDistM;

    // 1. Sinh node quanh các POI (cổng trường, cổng chợ, bến bus)
    for (const poi of pois) {
      if (poi.type === 'real_stall') continue; // Tiệm thực tế là mốc ngoại cảnh, không thể chiếm dụng làm node vỉa hè
      const tag = `${poi.type}_gate`;
      candidates.push({
        id: `node-${poi.id}`,
        x: poi.position.x + (Math.random() > 0.5 ? 8 : -8),
        y: poi.position.y + (Math.random() > 0.5 ? 8 : -8),
        sourcePoiId: poi.id,
        trafficBase: Math.min(1.0, 0.7 + (dna.pedestrianFlow * 0.3)),
        visibility: 0.85,
        density: dna.density,
        risk: Math.max(0.1, dna.risk * 0.8),
        unlockCost: 250_000,
        rentPerDay: 40_000,
        tags: [tag, 'poi_hotspot'],
      });
    }

    // 2. Sinh node phân bố đều tại các giao lộ hoặc điểm đẹp trên các tuyến phố
    for (const road of roads) {
      if (road.points.length < 2) continue;

      let roadNodesAdded = 0;
      for (let i = 0; i < road.points.length - 1; i++) {
        if (roadNodesAdded >= 2) break; // Mỗi con đường chỉ chọn tối đa 2 điểm kinh doanh chiến lược

        const p1 = road.points[i]!;
        const p2 = road.points[i + 1]!;
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        const isDedupe = candidates.some(
          (c) => Math.hypot(c.x - midX, c.y - midY) < minDedupeDist
        );

        if (!isDedupe) {
          const isPrimary = road.roadClass === 'primary';
          candidates.push({
            id: `node-road-${road.id}-${i}`,
            x: midX,
            y: midY,
            roadType: road.roadClass,
            trafficBase: isPrimary ? 0.85 : 0.65,
            visibility: isPrimary ? 0.9 : 0.6,
            density: dna.density,
            risk: isPrimary ? 0.4 : 0.2,
            unlockCost: isPrimary ? 350_000 : 200_000,
            rentPerDay: isPrimary ? 50_000 : 25_000,
            tags: [road.roadClass, 'sidewalk'],
          });
          roadNodesAdded++;
        }
      }
    }

    // Đảm bảo tối thiểu 5 nodes
    if (candidates.length < GAME_CONFIG.world.minNodes) {
      this.padFallbackNodes(candidates, dna);
    }

    return candidates.slice(0, 15); // Lấy tối đa 15 nodes tốt nhất cho MVP
  }

  private static padFallbackNodes(candidates: BusinessNode[], dna: NeighborhoodDNA): void {
    const offsets = [
      { x: -50, y: -60, tag: 'alley_cross' },
      { x: 70, y: 40, tag: 'corner_tea' },
      { x: -80, y: 90, tag: 'residential_gate' },
      { x: 60, y: -90, tag: 'market_lane' },
      { x: 0, y: 30, tag: 'center_spot' },
    ];

    for (let i = candidates.length; i < 5; i++) {
      const off = offsets[i % offsets.length]!;
      candidates.push({
        id: `node-fallback-${i}`,
        x: off.x,
        y: off.y,
        trafficBase: 0.7,
        visibility: 0.75,
        density: dna.density,
        risk: 0.25,
        unlockCost: 200_000,
        rentPerDay: 30_000,
        tags: [off.tag],
      });
    }
  }
}
